/**
 * Helpers para URLs de produtos
 * Centraliza lógica de geração de URLs amigáveis (canônico = slug).
 */

const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;
const DEFAULT_PUBLIC_SITE_URL = "https://www.emperc.com.br";

/**
 * True para localhost / loopback (links inúteis no WhatsApp e em share externo).
 */
export function isLocalHostUrl(value) {
  if (!value) return false;
  try {
    const host = new URL(value, "http://local").hostname.toLowerCase();
    return (
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "0.0.0.0" ||
      host === "::1" ||
      host.endsWith(".local")
    );
  } catch {
    return /localhost|127\.0\.0\.1/i.test(String(value));
  }
}

/**
 * Origem pública do site (sem barra final).
 * Ordem:
 * 1) REACT_APP_SITE_URL (se não for localhost)
 * 2) window.location.origin (se não for localhost — staging/produção)
 * 3) https://www.emperc.com.br
 *
 * Assim WhatsApp/share/SEO nunca levam localhost, mesmo em `npm start`.
 */
export function getPublicSiteUrl() {
  const fromEnv = String(process.env.REACT_APP_SITE_URL || "")
    .trim()
    .replace(/\/$/, "");

  if (fromEnv && !isLocalHostUrl(fromEnv)) {
    return fromEnv;
  }

  if (typeof window !== "undefined") {
    const origin = window.location.origin;
    if (origin && !isLocalHostUrl(origin)) {
      return origin.replace(/\/$/, "");
    }
  }

  return DEFAULT_PUBLIC_SITE_URL;
}

/**
 * Absolute URL a partir de um path (/products/...).
 */
export function getAbsoluteUrl(path = "/") {
  const base = getPublicSiteUrl();
  const normalized = String(path || "/").startsWith("/")
    ? String(path || "/")
    : `/${path}`;
  return `${base}${normalized}`;
}

/**
 * Gera URL canônica do produto
 * Prioriza slug, fallback para ID
 *
 * @param {Object} product - Objeto do produto
 * @returns {string} URL do produto
 */
export function getProductUrl(product) {
  if (!product) return "#";

  // Preferir caminho canônico da API quando disponível
  if (
    typeof product.canonicalPath === "string" &&
    product.canonicalPath.startsWith("/products/")
  ) {
    return product.canonicalPath;
  }

  const identifier = product.slug || product._id || product.id;

  if (!identifier) {
    console.warn("Produto sem slug ou ID:", product);
    return "#";
  }

  return `/products/${identifier}`;
}

/**
 * True quando a URL atual deve redirecionar para o slug canônico.
 */
export function shouldRedirectToCanonicalSlug(urlParam, product) {
  if (!product?.slug || !urlParam) return false;
  if (urlParam === product.slug) return false;
  return isObjectId(urlParam) || product.redirectToSlug === true;
}

/**
 * Gera URL completa (com domínio público) para produto.
 * Usa getPublicSiteUrl — nunca localhost em WhatsApp/share.
 *
 * @param {Object} product - Objeto do produto
 * @returns {string} URL completa do produto
 */
export function getProductFullUrl(product) {
  const path = getProductUrl(product);
  if (!path || path === "#") return getPublicSiteUrl();
  return getAbsoluteUrl(path);
}

/**
 * Verifica se uma URL é de produto antigo (usa ID)
 *
 * @param {string} url - URL para verificar
 * @returns {boolean} True se for URL antiga
 */
export function isOldProductUrl(url) {
  const oldUrlPattern = /^\/products\/[0-9a-f]{24}$/i;
  return oldUrlPattern.test(url);
}

/**
 * Extrai identificador (slug ou ID) de uma URL de produto
 *
 * @param {string} url - URL do produto
 * @returns {string|null} Identificador ou null
 */
export function extractProductIdentifier(url) {
  const match = url.match(/\/products\/([^/?#]+)/);
  return match ? match[1] : null;
}

/**
 * Verifica se um identificador é um ObjectId MongoDB
 *
 * @param {string} identifier - Identificador para verificar
 * @returns {boolean} True se for ObjectId
 */
export function isObjectId(identifier) {
  if (!identifier || typeof identifier !== "string") return false;
  return OBJECT_ID_RE.test(identifier);
}
